import {
  brandUrlSchema,
  HEARTH_DEFAULTS,
  loginLinkSchema,
  loginPageSchema,
  type LoginPage,
} from '@hearth/shared';
import { createFileRoute } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowUp,
  Images,
  Loader2,
  Monitor,
  Moon,
  Plus,
  Sun,
  Trash2,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  AssetImage,
  AssetPicker,
  UploadButton,
  useAsset,
  useUploadAsset,
} from '@/components/customisation/asset-picker';
import { ColourField } from '@/components/customisation/colour-field';
import { LoginFrame } from '@/components/customisation/login-frame';
import {
  ControlSection,
  PreviewFrame,
  StudioPanes,
} from '@/components/customisation/theme-studio-panes';
import { FormField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { publicSiteFromDraft } from '@/lib/customisation/site';
import { useSiteDraft } from '@/lib/customisation/site-draft';
import { useAuthenticatedSession } from '@/lib/session';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/admin/customisation/login')({ component: Page });

type TextKey = 'heading' | 'description' | 'footerText';

const MAX_LINKS = 6;

function fieldError(key: TextKey, value: string): string | undefined {
  const result = loginPageSchema.shape[key].safeParse(value);
  return result.success ? undefined : result.error.issues[0]?.message;
}

function linkError(field: 'label' | 'url', value: string): string | undefined {
  const result = loginLinkSchema.shape[field].safeParse(value);
  return result.success ? undefined : result.error.issues[0]?.message;
}

function Page() {
  const draft = useSiteDraft();
  const session = useAuthenticatedSession();
  const { theme } = useTheme();
  const tenant = draft.tenant;
  const { login } = draft.site;
  const [previewScheme, setPreviewScheme] = useState<'light' | 'dark'>(theme);
  const [picking, setPicking] = useState(false);
  const background = useAsset(tenant, login.backgroundImage);
  const upload = useUploadAsset(tenant);

  const set = <K extends keyof LoginPage>(key: K, value: LoginPage[K], label?: string) =>
    draft.update(
      (s) => {
        s.login[key] = value;
      },
      label ?? `Change login ${key}`,
    );
  const setText = (key: TextKey, value: string) => set(key, value === '' ? null : value);
  const setPromo = (patch: Partial<NonNullable<LoginPage['promo']>>) =>
    draft.update((s) => {
      s.login.promo = {
        title: '',
        body: '',
        linkUrl: null,
        linkLabel: null,
        ...(s.login.promo ?? {}),
        ...patch,
      };
    }, 'Edit promo panel');
  const setLinks = (mutate: (links: LoginPage['links']) => void, label: string) =>
    draft.update((s) => {
      mutate(s.login.links);
    }, label);

  const site = publicSiteFromDraft(draft.site);

  const controls = (
    <>
      <ControlSection title="Heading">
        <div className="grid gap-4">
          <FormField
            label="Heading"
            description="Blank shows your display name."
            error={fieldError('heading', login.heading ?? '')}
          >
            <Input
              value={login.heading ?? ''}
              onChange={(e) => setText('heading', e.target.value)}
              placeholder="Welcome back"
              maxLength={80}
            />
          </FormField>
          <FormField label="Description" error={fieldError('description', login.description ?? '')}>
            <Textarea
              value={login.description ?? ''}
              onChange={(e) => setText('description', e.target.value)}
              placeholder="Sign in to manage your game servers."
              rows={2}
              maxLength={300}
            />
          </FormField>
          <Toggle
            id="login-show-logo"
            label="Show the logo"
            description="The login slot, else the logo for the scheme (Branding tab)."
            checked={login.showLogo}
            onChange={(v) => set('showLogo', v)}
          />
        </div>
      </ControlSection>

      <ControlSection
        title="Background"
        description="A colour, an image, or the theme background with Hearth’s grid."
      >
        <div className="grid gap-4">
          <ColourField
            label="Background colour"
            description={
              login.backgroundColor
                ? 'Replaces the theme background.'
                : 'Using the theme background.'
            }
            value={login.backgroundColor}
            fallback={HEARTH_DEFAULTS[previewScheme].background}
            onChange={(v) => set('backgroundColor', v)}
          />
          <div className="flex gap-3">
            <AssetImage asset={background} size="sm" className="h-16 w-24 shrink-0" />
            <div className="grid min-w-0 flex-1 gap-1.5">
              <div>
                <p className="text-sm font-medium">Background image</p>
                <p className="text-muted-foreground truncate text-xs">
                  {background
                    ? `${background.filename}${background.width && background.height ? ` · ${background.width}×${background.height}` : ''}`
                    : login.backgroundImage
                      ? 'The image was deleted.'
                      : 'Up to 4096 px; JPEG or WebP keep it small.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <UploadButton
                  size="xs"
                  pending={upload.isPending}
                  onFile={(file) =>
                    upload.mutate(
                      { file, kind: 'background' },
                      { onSuccess: (a) => set('backgroundImage', a.id, 'Change background image') },
                    )
                  }
                />
                <Button type="button" size="xs" variant="outline" onClick={() => setPicking(true)}>
                  <Images /> Choose
                </Button>
                {login.backgroundImage && (
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    onClick={() => set('backgroundImage', null, 'Remove background image')}
                  >
                    <X /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
          <Toggle
            id="login-overlay"
            label="Darken the image"
            description="Keeps the heading and form readable over busy pictures."
            checked={login.overlay}
            onChange={(v) => set('overlay', v)}
            disabled={!login.backgroundImage}
          />
          <AssetPicker
            tenant={tenant}
            kinds={['background', 'image']}
            open={picking}
            onOpenChange={setPicking}
            selected={login.backgroundImage}
            title="Background image"
            onPick={(a) => {
              set('backgroundImage', a.id, 'Change background image');
              setPicking(false);
            }}
          />
        </div>
      </ControlSection>

      <ControlSection
        title="Layout"
        description="Where the form sits, and which scheme the page uses."
      >
        <div className="grid gap-4">
          <Choice
            label="Form position"
            value={login.formPosition}
            options={[
              { value: 'left', label: 'Left' },
              { value: 'center', label: 'Centre' },
              { value: 'right', label: 'Right' },
            ]}
            onChange={(v) => set('formPosition', v)}
          />
          <Choice
            label="Scheme"
            value={login.theme}
            options={[
              { value: 'inherit', label: 'Follow the visitor', icon: Monitor },
              { value: 'dark', label: 'Always dark', icon: Moon },
              { value: 'light', label: 'Always light', icon: Sun },
            ]}
            onChange={(v) => set('theme', v)}
          />
        </div>
      </ControlSection>

      <ControlSection
        title="Promo panel"
        description="A short message beside the form (below it when centred). Leave the title and text blank to hide it."
        actions={
          login.promo && (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() => set('promo', null, 'Remove promo panel')}
            >
              <X /> Remove
            </Button>
          )
        }
      >
        <div className="grid gap-4">
          <FormField label="Title">
            <Input
              value={login.promo?.title ?? ''}
              onChange={(e) => setPromo({ title: e.target.value })}
              maxLength={80}
              placeholder="New: Minecraft 1.21 servers"
            />
          </FormField>
          <FormField label="Text">
            <Textarea
              value={login.promo?.body ?? ''}
              onChange={(e) => setPromo({ body: e.target.value })}
              maxLength={600}
              rows={3}
              placeholder="Order a server today and it is online in minutes."
            />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,10rem)]">
            <FormField
              label="Link"
              error={
                login.promo?.linkUrl
                  ? brandUrlSchema.safeParse(login.promo.linkUrl).error?.issues[0]?.message
                  : undefined
              }
            >
              <Input
                type="url"
                inputMode="url"
                value={login.promo?.linkUrl ?? ''}
                onChange={(e) =>
                  setPromo({ linkUrl: e.target.value === '' ? null : e.target.value })
                }
                placeholder="https://example.com/pricing"
                spellCheck={false}
              />
            </FormField>
            <FormField label="Link label">
              <Input
                value={login.promo?.linkLabel ?? ''}
                onChange={(e) =>
                  setPromo({ linkLabel: e.target.value === '' ? null : e.target.value })
                }
                maxLength={40}
                placeholder="See plans"
              />
            </FormField>
          </div>
        </div>
      </ControlSection>

      <ControlSection
        title="Links and footer"
        description={`Up to ${MAX_LINKS} links under the form (terms, status page, Discord…). Public custom pages are linked automatically.`}
        actions={
          <Button
            type="button"
            size="xs"
            variant="outline"
            disabled={login.links.length >= MAX_LINKS}
            onClick={() =>
              setLinks((links) => {
                links.push({ label: '', url: '' });
              }, 'Add login link')
            }
          >
            <Plus /> Add link
          </Button>
        }
      >
        <div className="grid gap-4">
          {login.links.length === 0 && (
            <p className="text-muted-foreground text-xs">No links yet.</p>
          )}
          {login.links.map((link, i) => (
            <div
              key={i}
              className="bg-elevated/50 grid gap-3 rounded-lg border p-3 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)_auto]"
            >
              <FormField label="Label" error={linkError('label', link.label)}>
                <Input
                  value={link.label}
                  onChange={(e) =>
                    setLinks((links) => {
                      links[i]!.label = e.target.value;
                    }, 'Edit login link')
                  }
                  maxLength={60}
                  placeholder="Terms"
                />
              </FormField>
              <FormField label="URL" error={linkError('url', link.url)}>
                <Input
                  type="url"
                  inputMode="url"
                  value={link.url}
                  onChange={(e) =>
                    setLinks((links) => {
                      links[i]!.url = e.target.value;
                    }, 'Edit login link')
                  }
                  placeholder="https://example.com/terms"
                  spellCheck={false}
                />
              </FormField>
              <div className="flex items-end gap-0.5 sm:pb-0.5">
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Move up"
                  disabled={i === 0}
                  onClick={() =>
                    setLinks((links) => {
                      [links[i - 1], links[i]] = [links[i]!, links[i - 1]!];
                    }, 'Reorder login links')
                  }
                >
                  <ArrowUp />
                </Button>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Move down"
                  disabled={i === login.links.length - 1}
                  onClick={() =>
                    setLinks((links) => {
                      [links[i], links[i + 1]] = [links[i + 1]!, links[i]!];
                    }, 'Reorder login links')
                  }
                >
                  <ArrowDown />
                </Button>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Remove link"
                  onClick={() =>
                    setLinks((links) => {
                      links.splice(i, 1);
                    }, 'Remove login link')
                  }
                >
                  <Trash2 />
                </Button>
              </div>
            </div>
          ))}
          <FormField
            label="Footer text"
            description="Blank uses the footer text from Branding."
            error={fieldError('footerText', login.footerText ?? '')}
          >
            <Input
              value={login.footerText ?? ''}
              onChange={(e) => setText('footerText', e.target.value)}
              maxLength={200}
              placeholder="© Acme Hosting"
            />
          </FormField>
        </div>
      </ControlSection>
    </>
  );

  return (
    <StudioPanes
      title="Login page"
      description="What visitors see before they sign in. The preview is the real login frame with a sample form."
      wide
      controls={controls}
      previewLabel={
        login.theme === 'inherit'
          ? `Live preview · ${previewScheme}`
          : `Live preview · always ${login.theme}`
      }
      previewActions={
        login.theme === 'inherit' && (
          <div
            className="bg-muted inline-flex rounded-lg p-[3px]"
            role="group"
            aria-label="Preview theme"
          >
            {(['dark', 'light'] as const).map((s) => (
              <Button
                key={s}
                type="button"
                size="xs"
                variant={previewScheme === s ? 'outline' : 'ghost'}
                aria-pressed={previewScheme === s}
                onClick={() => setPreviewScheme(s)}
              >
                {s === 'dark' ? <Moon /> : <Sun />} {s === 'dark' ? 'Dark' : 'Light'}
              </Button>
            ))}
          </div>
        )
      }
      preview={
        <PreviewFrame>
          <LoginFrame site={site} panelName={session.panel.name} scheme={previewScheme} preview>
            <SampleForm />
          </LoginFrame>
        </PreviewFrame>
      }
    />
  );
}

/** A non-functional sign-in card, the size and shape of the real one. */
function SampleForm() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign in</CardTitle>
        <CardDescription>Use your email address or username.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="sample-login">Email or username</Label>
          <Input id="sample-login" defaultValue="alex@example.com" readOnly tabIndex={-1} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="sample-password">Password</Label>
          <Input
            id="sample-password"
            type="password"
            defaultValue="••••••••••"
            readOnly
            tabIndex={-1}
          />
        </div>
        <span className="text-muted-foreground -mt-2 justify-self-end text-xs">
          Forgot password?
        </span>
      </CardContent>
      <CardFooter className="mt-6 flex-col gap-3">
        <Button type="button" className="w-full" tabIndex={-1}>
          <Loader2 className="hidden" /> Sign in
        </Button>
      </CardFooter>
    </Card>
  );
}

function Toggle({
  id,
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-4', disabled && 'opacity-60')}>
      <div className="grid gap-0.5">
        <Label htmlFor={id}>{label}</Label>
        {description && <p className="text-muted-foreground text-xs">{description}</p>}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; icon?: typeof Sun }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <p className="text-sm leading-none font-medium">{label}</p>
      <div
        className="bg-muted inline-flex w-full flex-wrap rounded-lg p-[3px]"
        role="group"
        aria-label={label}
      >
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={value === o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              'focus-visible:ring-ring/50 inline-flex h-7 flex-1 items-center justify-center gap-1.5 rounded-md px-2 text-xs font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px]',
              value === o.value
                ? 'bg-background text-foreground shadow-sm dark:bg-input/30 dark:border-input dark:border'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {o.icon && <o.icon className="size-3.5" aria-hidden />}
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
